import { Theme, css } from '@emotion/react';

import { useRouter } from 'next/router';

import { useEffect } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Icon } from 'chatie-design-system/icon';

import Avatar, { AvatarSize } from '@components/common/Avatar';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IChatCharacter } from '@model';
import { media, multilineEllipsis } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import { setFlow } from '@utils/misc';
import { useWebView } from '@utils/webview';

const containerCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  padding: 8px ${Spacing.medium}px 12px;
  gap: 12px;

  ${media.tablet} {
    padding: 8px ${Spacing.xlarge}px 12px;
  }
  ${media.desktop} {
    padding: 8px ${Spacing.xxlarge}px 12px;
  }

  .title {
    ${Typography.h6}
  }

  .shadow {
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.2);
    border-radius: ${Radius.small}px;
  }

  .btn {
    position: relative;
    padding: 12px;
  }
  .btn::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${Radius.small}px;
    padding: 1px;
    background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
  .content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .profile {
    display: flex;
    gap: 12px;
    flex: 1;
  }

  .text {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    gap: 4px;

    color: ${theme.var.gray800};
    ${Typography.body14_sentence};

    h5 {
      color: ${theme.var.gray900};
      ${Typography.h5}
      white-space: wrap;
      ${multilineEllipsis(1)};
    }
    p {
      white-space: wrap;
      ${multilineEllipsis(1)};
    }
  }

  .arrow {
    display: flex;
    align-items: center;
    ${Typography.h5};
    gap: 4px;
    background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

interface CharacterDetailCta {
  titleText?: string;
  character: IChatCharacter;
}

const CharacterDetailCta = ({
  titleText = '성격만 보면 최애 확정🔥',
  character,
}: CharacterDetailCta) => {
  const router = useRouter();
  const { web } = useWebView();
  const { platform } = useGlobalContext();
  const { firebase } = useAnalytics();

  const handleNavigateDetail = () => {
    firebase.logEvent('story_character_click', {
      character_id: character.id,
      story_id: character.story_id,
    });

    if (platform === Platform.web) {
      router.push(`/play/characters/${character.id}`);
      return;
    }
    web.openPage(`/play/characters/${character.id}`);
  };

  useEffect(() => {
    setFlow({ name: 'CTA' });
  }, []);

  return (
    <div css={containerCSS}>
      <p className="title">{titleText}</p>
      <div className="shadow">
        <div className="btn" onClick={handleNavigateDetail}>
          <div className="content">
            <div className="profile">
              <Avatar
                size={AvatarSize.largeSquare}
                picture={character.profile_image}
              />
              <div className="text">
                <h5>{character.name}</h5>
                <p className="multiline">
                  {`${character.information.age}세 | ${character.information.job}`}
                </p>
              </div>
            </div>
            <div className="arrow">
              <h5>프로필 보기</h5>
              <Icon
                icon={ArrowRight}
                size={Sizes.xsmall}
                color={'rgba(21, 144, 212, 1)'}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterDetailCta;
